<template>
  <span>
    <template v-for="(item, index) in computedItems">
      <v-list-item
        :key="item._id"
        :disabled="item.current_status == 'cancelled' && subOrder.current_status == 'to_pickup'"
      >
        <v-list-item-avatar tile size="100">
          <v-img :src="item.product_image_link || defaultImage"></v-img>
        </v-list-item-avatar>
        <v-list-item-content>
          <v-list-item-content class="pb-2 pt-0">{{ item.product_title }}</v-list-item-content>
          <v-list-item-subtitle v-if="item.is_customized" class="primary--text">
            {{ $t('product.customization') }}: {{ $t('product.yes') }}
          </v-list-item-subtitle>
          <v-list-item-subtitle v-if="item.product_sku">
            {{ $t('product.sku') }}: {{ item.product_sku }}
          </v-list-item-subtitle>
          <v-list-item-subtitle v-if="item.barcode || item.product_ean">
            {{ $t('product.barcode') }}: {{ item.barcode || item.product_ean | formatBarCode() }}
          </v-list-item-subtitle>
          <v-list-item-subtitle v-if="item.product_size">
            {{ $t('product.size') }}: {{ item.product_size }}
          </v-list-item-subtitle>
          <v-list-item-subtitle v-if="item.product_color">
            {{ $t('product.color') }}: {{ item.product_color }}
          </v-list-item-subtitle>
          <v-list-item-subtitle>
            {{ $t('product.price') }}:
            <del v-if="item.product_default_price && item.product_default_price > item.product_sale_price">
              {{ item.product_default_price | formatPrice(subOrder.currency) }}
              {{ subOrder.currency ? subOrder.currency.code : subOrder.currency_code }}
            </del>
            {{ item.product_sale_price | formatPrice(subOrder.currency) }}
            {{ subOrder.currency ? subOrder.currency.code : subOrder.currency_code }}
          </v-list-item-subtitle>

          <v-list-item-subtitle v-if="subOrder.current_status == 'to_pickup'">
            {{ $t('product.quantityToHandOver') }}:
            {{ item.current_status == 'cancelled' ? 0 : item.validated_quantity }}
          </v-list-item-subtitle>
          <v-list-item-subtitle
            v-else-if="isScanStep && item.current_status !== 'cancelled'"
            :class="{ isScanned: itemIsEntirelyScanned(item, item.nb_scanned) }"
          >
            {{ $t('product.preparedQuantity') }}: {{ item.nb_scanned ? item.nb_scanned : 0 }}/
            <span v-if="item.current_status == 'partially_validated'">{{ item.validated_quantity }}</span>
            <span v-else>{{ item.product_quantity }}</span>
          </v-list-item-subtitle>
          <v-list-item-subtitle v-else>
            {{ $t('product.quantity') }}: {{ item.validated_quantity ? item.validated_quantity : 0 }}/{{
              item.product_quantity
            }}
          </v-list-item-subtitle>
          <v-skeleton-loader v-if="isStockLoading" max-width="160" type="text"></v-skeleton-loader>
          <span v-else-if="item.stock !== undefined && item.stock !== null">
            <v-list-item-subtitle>
              {{ $t('product.availableStock') }}:
              <v-badge inline dot left :color="item.stock_color">
                <span>{{ item.stock }}</span>
              </v-badge>
              <span style="white-space: break-spaces">{{ storeName }}</span>
            </v-list-item-subtitle>

            <v-list-item-subtitle v-if="Array.isArray(item.product_positions) && item.product_positions.length">
              <div v-for="(productPosition, index) in item.product_positions" v-bind:key="index" class="pl-4">
                <v-badge inline dot left :color="productPosition.stock_color">
                  <span class="pr-1">{{ productPosition.stock }}</span>
                  <span v-if="Array.isArray(productPosition.position_details)">
                    <span v-for="(positionDetail, index) in productPosition.position_details" v-bind:key="index">
                      {{ ' - ' + positionDetail.name }} {{ positionDetail.value }}
                    </span>
                  </span>
                </v-badge>
                <br />
              </div>
            </v-list-item-subtitle>
          </span>
        </v-list-item-content>

        <v-list-item-action class="align-right" style="max-width: 210px; justify-content: flex-start">
          <v-btn
            v-if="item.custom && item.custom.length > 0"
            elevation="0"
            small
            @click="openCustomItemFieldsDialog(item)"
          >
            {{ $t('product.custom.title') }}
          </v-btn>
          <span v-if="item.current_status == 'validated'" class="primary--text text-right list-item-action-text">
            {{ $t('product.product') }} {{ $t('product.validated') }}
          </span>
          <span
            v-else-if="item.current_status == 'partially_validated'"
            class="primary--text text-right list-item-action-text"
          >
            {{ $t('product.product') }} {{ $t('product.partiallyValidated') }}
          </span>
          <span v-else-if="item.current_status == 'cancelled'" class="text-right list-item-action-text">
            <p class="mb-0 text-uppercase">{{ $t('product.product') }} {{ $t('product.cancelled') }}</p>
            <p class="mb-0 text-uppercase" v-if="item.cancel_reason">
              {{ getItemCancelReasonsTranslation(item.cancel_reason) }}
            </p>
          </span>
          <span v-if="displayActions">
            <span
              v-if="
                subOrder.current_status == 'waiting' &&
                isStorePreparation &&
                (subOrder.source_location_id == currentStore || isCustomerService)
              "
              class="align-right"
            >
              <v-btn
                class="mt-4"
                :large="!isMobile"
                :medium="isMobile"
                color="primary"
                depressed
                tile
                @click="openProductValidationModal(item)"
              >
                <v-icon>mdi-check</v-icon>
              </v-btn>
              <v-btn
                class="mt-4"
                :large="!isMobile"
                :medium="isMobile"
                depressed
                tile
                @click="openCancelProductModal(item)"
              >
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </span>
            <v-btn
              v-else-if="mustPickupProductLocation(item.product_positions)"
              class="mt-4 break-space-button"
              :large="!isMobile"
              :medium="isMobile"
              color="primary"
              depressed
              tile
              @click="openProductPositionModal(item)"
            >
              {{ $t('product.selectPositions') }}
            </v-btn>
            <v-list-item-subtitle
              v-for="(selectedPosition, index) in getItemSelectedPositions(item)"
              v-bind:key="index"
              class="pt-1 mr-0 text-right"
              style="white-space: normal; flex: auto"
            >
              {{ formatPositionDisplay(selectedPosition) }}
            </v-list-item-subtitle>
          </span>
        </v-list-item-action>
      </v-list-item>
      <v-col :key="'PickupIndex' + index" class="text-center pb-1">
        <v-card-text
          v-if="mustPickupProductLocation(item.product_positions) && remainingQuantityToPick(item) > 0"
          class="red--text py-0"
        >
          {{
            $t('product.remainingQuantityWithoutIndicatedPickupPosition', {
              noPosNumber: remainingQuantityToPick(item),
            })
          }}
        </v-card-text>
      </v-col>
      <v-expansion-panels
        v-if="Array.isArray(item.customizations) && item.customizations.length"
        :key="'EP' + index"
        flat
        v-model="customizationsPanel[index]"
        multiple
        accordion
      >
        <v-expansion-panel>
          <v-expansion-panel-header class="pb-0">
            <span v-if="customizationsPanel[index].includes(0)" class="primary--text text-uppercase">
              {{ $t('product.hideProductCustomizations') }}
            </span>
            <span v-else class="primary--text text-uppercase">{{ $t('product.seeProductCustomizations') }}</span>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <v-row>
              <v-col
                cols="12"
                lg="4"
                :key="indexCustom"
                v-for="(customization, indexCustom) of item.customizations"
                class="pt-0"
              >
                <v-list-item-title class="font-weight-bold" v-if="customization.display_label">
                  {{ customization.display_label }}
                </v-list-item-title>
                <v-list-item-title class="font-weight-bold" v-else>
                  {{ customization.custom_service_id }}
                </v-list-item-title>
                <v-list-item-subtitle
                  :key="indexProperty"
                  v-for="(property, indexProperty) of customization.properties"
                  style="white-space: normal"
                >
                  <span>{{ property.name }}:</span>
                  <span style="text-transform: none">{{ property.value }}</span>
                </v-list-item-subtitle>
              </v-col>
            </v-row>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
      <v-col :key="index" class="text-center pt-0 pl-0 pr-0">
        <v-divider v-if="index != subOrder.items.length - 1" />
      </v-col>
    </template>
    <cancel-sub-order-dialog
      v-if="dialogCancelProduct"
      v-model="dialogCancelProduct"
      :choices="itemCancelReasonsClone"
      :title="$t('subOrder.cancelProduct')"
      :introductionSentence="$t('subOrder.cancelReasonText')"
      @confirm="validateCancelReason"
    ></cancel-sub-order-dialog>
    <product-validation-dialog
      v-if="productValidationDialog"
      v-model="productValidationDialog"
      @validateProductQuantity="validateProductQuantity($event)"
      :item="targetItem"
    ></product-validation-dialog>
    <item-custom-fields
      v-if="customItems"
      v-model="showCustomItemFieldsDialog"
      :customs="customItems"
      @close="closeCustomFieldsDialog()"
    />
    <product-position-choice-dialog
      v-if="targetItem"
      v-model="showProductPositionChoiceDialog"
      :choices="targetItem.product_positions"
      :product="targetItem"
      @confirm="$emit('destroy-product-list')"
    ></product-position-choice-dialog>
  </span>
</template>

<script>
  import ProductValidationDialog from '@/components/SubOrders/ProductValidationDialog.vue'
  import CancelSubOrderDialog from '@/components/MultipleChoicesDialog.vue'
  import ItemCustomFields from '@/components/dialogs/ItemCustomFields.vue'
  import ProductPositionChoiceDialog from '@/components/dialogs/ProductPositionChoiceDialog'
  import { mapState } from 'vuex'

  export default {
    name: 'productList',
    components: {
      ProductValidationDialog,
      CancelSubOrderDialog,
      ItemCustomFields,
      ProductPositionChoiceDialog,
    },
    data() {
      return {
        dialogCancelProduct: false,
        targetItem: undefined,
        productCancelReason: undefined,
        defaultImage: require('@/assets/img/products/product_default.png'),
        productValidationDialog: false,
        showCustomItemFieldsDialog: false,
        customItems: null,
        showProductPositionChoiceDialog: false,
        itemCancelReasonsClone: [],
      }
    },
    props: {
      subOrder: {
        type: Object,
        required: true,
      },
      isScanStep: {
        type: Boolean,
        default: false,
      },
      isStockLoading: {
        type: Boolean,
        default: false,
      },
      customizationsPanel: {
        type: Array,
        default: () => {
          return []
        },
      },
      displayProductsCustomCancelReason: {
        type: Boolean,
        default: true,
      },
      sourceLocation: {
        type: Object,
      },
      itemCancelReasons: {
        type: Array,
        default: () => {
          return []
        },
      },
      displayActions: {
        type: Boolean,
        default: true,
      },
    },
    methods: {
      updateItem: function (item, status) {
        this.$set(item, 'current_status', status)
      },
      openCancelProductModal: function (item) {
        this.targetItem = item
        this.dialogCancelProduct = true
      },
      openProductPositionModal: function (item) {
        this.$emit('open-product-position-modal')
        this.targetItem = item
        this.showProductPositionChoiceDialog = true
      },
      openProductValidationModal: function (item) {
        if (item.product_quantity > 1) {
          this.targetItem = item
          this.productValidationDialog = true
        } else {
          this.updateItem(item, 'validated')
          this.$set(item, 'validated_quantity', item.product_quantity)
        }
      },
      closeCancelProductModal: function () {
        this.productCancelReason = undefined
        this.targetItem = undefined
        this.dialogCancelProduct = false
      },
      validateCancelReason: function (productCancelReason) {
        this.$set(this.targetItem, 'cancel_reason', productCancelReason)
        this.updateItem(this.targetItem, 'cancelled')
        this.$set(this.targetItem, 'validated_quantity', 0)
        this.closeCancelProductModal()
      },
      validateProductQuantity: function (confirmedQuantity) {
        let status = 'validated'
        if (this.targetItem.product_quantity != confirmedQuantity) {
          status = 'partially_validated'
        }
        this.updateItem(this.targetItem, status)
        this.$set(this.targetItem, 'validated_quantity', confirmedQuantity)
        this.productValidationDialog = false
      },
      getItemCancelReasonsTranslation: function (itemCancelReasonValue) {
        let cancelReason = this.itemCancelReasonsClone.find(
          (cancelReason) => cancelReason.value == itemCancelReasonValue
        )
        let unselectableItemCancelReason = [
          { text: this.$t('subOrder.cancelledByPreparator'), value: 'product_cancelled_by_preparator' },
          { text: this.$t('subOrder.itemNotBought'), value: 'item_not_bought' },
          { text: this.$t('subOrder.timeout'), value: 'timeout' },
          { text: this.$t('subOrder.partialNotAuthorized'), value: 'partial_not_authorized' },
          { text: this.$t('subOrder.clientChangeOfMind'), value: 'client_changed_his_mind' },
          { text: this.$t('subOrder.deterioratedProducts'), value: 'deteriorated_products' },
          { text: this.$t('subOrder.orderedMultipleTimes'), value: 'ordered_multiple_times' },
          { text: this.$t('subOrder.productsNotAvailable'), value: 'products_not_available' },
          { text: this.$t('subOrder.customerDidNotCollect'), value: 'customer_did_not_collect_order' },
          { text: this.$t('subOrder.productNotValidated'), value: 'product_not_validated' },
          {
            text: this.$t('subOrder.customerDidNotCollectHisReservation'),
            value: 'customer_did_not_collect_his_reservation',
          },
        ]
        if (!cancelReason) {
          cancelReason = unselectableItemCancelReason.find(
            (cancelReason) => cancelReason.value == itemCancelReasonValue
          )
        }
        if (cancelReason) {
          return cancelReason.text
        } else return itemCancelReasonValue
      },
      itemIsEntirelyScanned(item, nbScanned) {
        const qtyScanned = nbScanned ? nbScanned : 0
        return (
          (item.current_status == 'partially_validated' && qtyScanned >= item.validated_quantity) ||
          (item.current_status == 'validated' && qtyScanned >= item.product_quantity)
        )
      },
      openCustomItemFieldsDialog(item) {
        this.showCustomItemFieldsDialog = true
        this.customItems = item.custom
      },
      closeCustomFieldsDialog() {
        this.showCustomItemFieldsDialog = false
      },
      getNbItems(validatedQuantity) {
        // get validated_quantity of an item for select option
        var nbItems = [0]
        for (var i = 1; i <= validatedQuantity; i++) {
          nbItems.push(i)
        }
        return nbItems
      },
      mustPickupProductLocation(productPositions) {
        return this.isScanStep && Array.isArray(productPositions) && productPositions.length > 0
      },
      getItemSelectedPositions(item) {
        if (Array.isArray(item.selected_product_positions)) {
          return item.selected_product_positions
        } else {
          return []
        }
      },
      formatPositionDisplay(selectedPosition) {
        if (Array.isArray(selectedPosition.custom) && selectedPosition.custom.length) {
          let displayText = selectedPosition.taken_quantity + ' ' + this.$t('product.takenOn') + ' '
          for (let positionDetail of selectedPosition.custom) {
            displayText +=
              (positionDetail.name === 'customPosition' ? '' : positionDetail.name) + ' ' + positionDetail.value + ' '
          }
          return displayText
        }
      },
      remainingQuantityToPick: function (product) {
        const productSelectedProductPositions = Array.isArray(product.selected_product_positions)
          ? product.selected_product_positions
          : []
        let remainingQuantityToPick =
          product.validated_quantity -
          productSelectedProductPositions.reduce(
            (productValidatedQuantity, position) =>
              parseInt(productValidatedQuantity) + parseInt(position.taken_quantity),
            0
          )
        return remainingQuantityToPick
      },
    },
    computed: {
      isMobile() {
        return this.$vuetify.breakpoint.width < 650
      },
      ...mapState(['currentStore', 'isCustomerService', 'isDark', 'isAdmin', 'currentStoreDetails']),
      isStorePreparation: function () {
        return ['click_and_collect', 'click_and_reserve', 'ship_from_store', 'inter_store'].includes(this.subOrder.type)
      },
      computedItems: function () {
        if (this.subOrder.current_status == 'partially_confirmed' && !this.isCustomerService) {
          return this.subOrder.items.filter((item) => item.current_status != 'cancelled')
        } else {
          return this.subOrder.items
        }
      },
      storeName: function () {
        if (this.sourceLocation && this.sourceLocation.name) {
          return '- ' + this.sourceLocation.name
        } else if (!this.subOrder.source_location_id) {
          return '- ' + this.currentStoreDetails.name
        }
        return ''
      },
    },
    mounted() {
      this.itemCancelReasonsClone = JSON.parse(JSON.stringify(this.itemCancelReasons))
      if (this.displayProductsCustomCancelReason) {
        this.itemCancelReasonsClone.push({ text: this.$t('subOrder.otherReason'), value: 'other' })
      }
    },
    destroyed() {
      this.$emit('destroy-product-list')
    },
  }
</script>

<style scoped>
  .isScanned {
    color: green !important;
  }
  .list-item-action-text {
    white-space: break-spaces;
    max-width: 200px;
  }
  .break-space-button {
    max-width: 150px;
    display: flow;
    white-space: break-spaces;
  }
</style>
